import { useLogger } from '@core/composables/useLogger'

interface LoggerLike {
  error: (msg: string, payload?: unknown) => void
}

const CHUNK_RELOAD_GUARD_KEY = '__chunk_reload_ts'
const CHUNK_RELOAD_GUARD_MS = 10_000

/**
 * Recovers from bundle/chunk-load failures (e.g. vite:preloadError,
 * Nuxt app:chunkError) by forcing a single page reload so the client
 * picks up the current build manifest and drops any stale hashes it
 * was holding. Guarded so back-to-back failures never trigger a reload
 * loop. Exported for testing.
 */
export function triggerChunkReload(err: unknown, source: string, logger: LoggerLike, win: Window = window): void {
  try {
    const now = Date.now()
    const last = Number(win.sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) ?? '0')
    if (now - last < CHUNK_RELOAD_GUARD_MS) {
      logger.error('🔴 [CHUNK RELOAD LOOP — SKIPPING]', {
        payload: {
          source,
          message: err instanceof Error ? err.message : String(err),
          timestamp: new Date().toISOString(),
        },
      })
      return
    }
    win.sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, String(now))
    logger.error('🔴 [CHUNK LOAD FAILED — RELOADING]', {
      payload: {
        source,
        message: err instanceof Error ? err.message : String(err),
        timestamp: new Date().toISOString(),
      },
    })
    win.location.reload()
  } catch (reloadErr) {
    logger.error('🔴 [CHUNK RELOAD HANDLER FAILED]', {
      payload: {
        message: reloadErr instanceof Error ? reloadErr.message : String(reloadErr),
        timestamp: new Date().toISOString(),
      },
    })
  }
}

/**
 * Error handler plugin - shared across all apps
 * Provides consistent error handling with structured logging
 */
export default defineNuxtPlugin(nuxtApp => {
  const { logger } = useLogger()

  try {
    // Handle unhandled promise rejections (only in Node.js environment)
    if (typeof process !== 'undefined' && process.on) {
      // Remove existing listeners to prevent duplicates during HMR
      process.removeAllListeners('unhandledRejection')
      process.removeAllListeners('uncaughtException')

      process.on('unhandledRejection', (reason, promise) => {
        logger.error('🔴 [UNHANDLED REJECTION]', {
          payload: {
            reason,
            promise,
            stack: reason instanceof Error ? reason.stack : undefined,
            timestamp: new Date().toISOString(),
          },
        })
      })

      // Handle uncaught exceptions (only in Node.js environment)
      process.on('uncaughtException', error => {
        logger.error('🔴 [UNCAUGHT EXCEPTION]', {
          payload: {
            message: error?.message,
            stack: error?.stack,
            timestamp: new Date().toISOString(),
          },
        })
      })
    }

    // Handle Nuxt errors (including SSR errors)
    nuxtApp.hooks.hook('app:error', error => {
      // Defensive error handling - ensure error is not undefined/null
      if (!error) {
        logger.error('🔴 [NUXT APP ERROR] - Undefined error object', {
          payload: {
            message: 'Received undefined error object',
            timestamp: new Date().toISOString(),
          },
        })
        return
      }

      logger.error('🔴 [NUXT APP ERROR]', {
        payload: {
          message: error?.message || 'Unknown error',
          stack: error?.stack,
          statusCode: error?.statusCode,
          statusMessage: error?.statusMessage,
          fatal: error?.fatal,
          timestamp: new Date().toISOString(),
        },
      })
    })

    // Handle Vue errors (including SSR errors)
    nuxtApp.vueApp.config.errorHandler = (error, instance, info) => {
      logger.error('🔴 [VUE ERROR]', {
        payload: {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          component: instance?.$options?.name || 'Unknown',
          info,
          timestamp: new Date().toISOString(),
        },
      })
    }

    // Handle global errors
    nuxtApp.vueApp.config.globalProperties.$onError = (error: unknown, context?: string) => {
      logger.error('🔴 [GLOBAL ERROR]', {
        payload: {
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          context,
          timestamp: new Date().toISOString(),
        },
      })
    }

    // Recover from bundle/chunk-load failures by reloading once. After a deploy
    // an old client holds stale build hashes; fetching those chunks 404s/5xx's
    // at the origin and every subsequent navigation stays broken until reload.
    if (typeof window !== 'undefined') {
      // Named function declaration so the listener can be removed on HMR
      // dispose — arrow listeners are flagged by our lint rules because they
      // cannot be referenced by removeEventListener.
      function onVitePreloadError(event: Event) {
        event.preventDefault()
        triggerChunkReload(event, 'vite:preloadError', logger, window)
      }
      window.addEventListener('vite:preloadError', onVitePreloadError)

      if (import.meta.hot) {
        import.meta.hot.dispose(() => {
          window.removeEventListener('vite:preloadError', onVitePreloadError)
        })
      }

      nuxtApp.hooks.hook('app:chunkError', ({ error }) => {
        triggerChunkReload(error, 'app:chunkError', logger, window)
      })
    }
  } catch (pluginError) {
    // If the plugin itself has an error, log it but don't break the app
    logger.error('🔴 [ERROR HANDLER PLUGIN ERROR]', {
      payload: {
        message: pluginError instanceof Error ? pluginError.message : 'Unknown plugin error',
        stack: pluginError instanceof Error ? pluginError.stack : undefined,
        timestamp: new Date().toISOString(),
      },
    })
  }
})
